import {patientNameFromRecord} from '../../utils/patientNames';

const COMBINING_MARK_RE = /[\u0300-\u036f]/g;
const TOKEN_SPLIT_RE = /[^\p{L}\p{N}]+/u;

const cleanValue = (value: unknown) => String(value ?? '').trim();

const foldNameSearchText = (value: unknown) =>
  cleanValue(value)
    .normalize('NFD')
    .replace(COMBINING_MARK_RE, '')
    .toLowerCase();

export const newEncounterNameSearchTokens = (value: unknown): string[] =>
  foldNameSearchText(value)
    .split(TOKEN_SPLIT_RE)
    .map(token => token.trim())
    .filter(Boolean);

const nameSources = (record: Record<string, any> = {}) => {
  const name = patientNameFromRecord(record);
  return {
    first: name.firstName,
    middle: name.middleName,
    last: name.lastName,
    displayName: name.displayName,
    fullName: name.fullName,
    patientName: name.patientName,
  };
};

const uniqueTokenSequences = (sequences: string[][]) => {
  const seen = new Set<string>();
  const result: string[][] = [];

  for (const sequence of sequences) {
    const tokens = sequence.filter(Boolean);
    if (!tokens.length) continue;
    const key = tokens.join('\u0000');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(tokens);
  }

  return result;
};

export const newEncounterPatientNameTokenSequences = (
  record: Record<string, any> = {},
  {includeLastFirst = false}: Record<string, any> = {},
) => {
  const {first, middle, last, displayName, fullName, patientName} =
    nameSources(record);
  const sequences = [
    newEncounterNameSearchTokens(fullName),
    newEncounterNameSearchTokens(displayName),
    newEncounterNameSearchTokens(patientName),
  ];

  if (includeLastFirst) {
    sequences.push(
      newEncounterNameSearchTokens(
        [last, first, middle].filter(Boolean).join(' '),
      ),
    );
  }

  return uniqueTokenSequences(sequences);
};

const orderedPrefixTokensMatch = (
  queryTokens: string[],
  nameTokens: string[],
) => {
  if (!queryTokens.length) return true;
  if (!nameTokens.length) return false;

  let nameIndex = 0;
  for (const queryToken of queryTokens) {
    let matched = false;
    while (nameIndex < nameTokens.length) {
      if (nameTokens[nameIndex].startsWith(queryToken)) {
        matched = true;
        nameIndex += 1;
        break;
      }
      nameIndex += 1;
    }
    if (!matched) return false;
  }
  return true;
};

export function newEncounterPatientMatchesNamePrefix(
  query: unknown,
  patientLike: Record<string, any> = {},
): boolean {
  const queryTokens = newEncounterNameSearchTokens(query);
  if (!queryTokens.length) return true;
  const includeLastFirst = cleanValue(query).includes(',');
  return newEncounterPatientNameTokenSequences(patientLike, {
    includeLastFirst,
  }).some(nameTokens => orderedPrefixTokensMatch(queryTokens, nameTokens));
}

export const newEncounterFirstNamePrefix = (query: unknown): string =>
  newEncounterNameSearchTokens(query)[0] || '';
